import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { isReasonCancelled, useReasonDialog } from "@/shared/ui/ReasonDialog";
import { Can } from "@/auth/permissions";
import { Pill, LoadingState } from "@/shared/ui/PageHeader";
import { openApiAdmin, type AdminCallLog, type AdminDelivery, type WebhookEndpointRow } from "@/services/openApi";

/**
 * 同行详情「开放 API」标签的运维区（FIX-3 #9 / FIX-4）：推送地址 + 推送记录（只读）+ 停用 / 恢复、按请求编号查调用日志、IP 白名单（只读）。
 * 停用推送必须填原因（REASON-1 场景 openapi_webhook_disable）；恢复不要原因。
 */
type Env = "test" | "live";
const ENV_LABEL: Record<Env, string> = { test: "测试", live: "正式" };
const fmt = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }) : "—");
const card = "rounded-xl border border-slate-200 bg-white p-4";
const th = "py-1 pr-3 font-medium";

function Drawer({ title, onClose, children, testId }: { title: string; onClose: () => void; children: ReactNode; testId: string }) {
  return (
    <div className="fixed inset-0 z-[60] flex justify-end bg-black/30 backdrop-blur-[1px] animate-in fade-in" role="dialog" aria-modal="true" data-testid={testId} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="h-full w-full max-w-xl overflow-y-auto bg-white p-5 shadow-xl animate-in slide-in-from-right">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-[15px] font-semibold text-slate-900">{title}</h3>
          <button type="button" onClick={onClose} aria-label="关闭" className="rounded-lg p-1 text-slate-500 hover:bg-slate-100"><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
const Json = ({ v }: { v: unknown }) => <pre className="max-h-64 overflow-auto rounded-lg bg-slate-900 p-3 text-[12px] leading-5 text-slate-100">{JSON.stringify(v ?? {}, null, 2)}</pre>;
const Err = ({ what, onRetry, testId }: { what: string; onRetry: () => void; testId: string }) => (
  <div role="alert" data-testid={testId} className="flex items-center gap-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-700">
    <span>{what}加载失败，请重试。</span><button type="button" onClick={onRetry} className="rounded border border-rose-300 bg-white px-2 py-0.5 hover:bg-rose-100">重试</button>
  </div>
);

export function PartnerOpenApiOps({ partnerId }: { partnerId: string }) {
  const qc = useQueryClient();
  const { ask: askReason, dialog: reasonDialog } = useReasonDialog();
  const [err, setErr] = useState<string | null>(null);
  const eps = useQuery({ queryKey: ["open-api", "partner-endpoints", partnerId], queryFn: () => openApiAdmin.webhookEndpoints(partnerId) });
  const ip = useQuery({ queryKey: ["open-api", "partner-ip", partnerId], queryFn: () => openApiAdmin.ipAllowlist(partnerId) });
  const refresh = () => qc.invalidateQueries({ queryKey: ["open-api"] });

  // 推送记录
  const [showDeliveries, setShowDeliveries] = useState(false);
  const [dEnv, setDEnv] = useState<Env>("live");
  const [dResult, setDResult] = useState("");
  const [dPage, setDPage] = useState(1);
  const [viewDelivery, setViewDelivery] = useState<AdminDelivery | null>(null);
  const deliveries = useQuery({
    queryKey: ["open-api", "partner-deliveries", partnerId, dEnv, dResult, dPage],
    queryFn: () => openApiAdmin.webhookDeliveries(partnerId, { env: dEnv, result: dResult, page: dPage }),
    enabled: showDeliveries,
  });

  // 调用日志
  const [rid, setRid] = useState("");
  const [searched, setSearched] = useState<string | null>(null);
  const [logDetail, setLogDetail] = useState<{ id: string } | null>(null);
  const logs = useQuery({ queryKey: ["open-api", "partner-calllogs", partnerId, searched], queryFn: () => openApiAdmin.callLogs(partnerId, { request_id: searched ?? undefined }), enabled: searched !== null });
  const detail = useQuery({ queryKey: ["open-api", "partner-calllog", partnerId, logDetail?.id], queryFn: () => openApiAdmin.callLog(partnerId, logDetail!.id), enabled: !!logDetail });

  const enable = useMutation({
    mutationFn: (env: Env) => openApiAdmin.enableWebhook(partnerId, env),
    onSuccess: () => { setErr(null); refresh(); },
    onError: (e) => setErr((e as Error).message || "恢复失败"),
  });
  async function disable(ep: WebhookEndpointRow) {
    setErr(null);
    try {
      const r = await askReason({ scene: "openapi_webhook_disable", title: `停用${ENV_LABEL[ep.env]}环境推送地址`, subtitle: `${ep.url}（停用后立即不再推送，对方在门户改不回来）`, required: true, danger: true, confirmText: "确认停用" });
      if (!r) return;
      await openApiAdmin.disableWebhook(partnerId, ep.env, r);
      refresh();
    } catch (e) { if (!isReasonCancelled(e)) setErr((e as Error).message || "停用失败"); }
  }

  const status = (ep: WebhookEndpointRow) =>
    ep.disabled ? <Pill tone="danger">已停用</Pill>
      : !ep.url ? <Pill tone="muted">未配置</Pill>
        : ep.paused_until ? <Pill tone="warning">连续失败，暂停到 {fmt(ep.paused_until)}</Pill>
          : <Pill tone="success">正常</Pill>;

  return (
    <>
      <div className={card} data-testid="oa-ops-webhooks">
        <div className="mb-2 flex items-center gap-2 text-[14px] font-semibold text-slate-900">推送地址 <button type="button" onClick={() => setShowDeliveries((v) => !v)} data-testid="oa-show-deliveries" className="ml-auto rounded-lg border border-slate-300 px-2.5 py-1 text-[12px] font-medium text-slate-600 hover:bg-slate-50">{showDeliveries ? "收起推送记录" : "查看推送记录"}</button></div>
        {eps.isLoading ? <LoadingState /> : eps.isError ? <Err what="推送地址" onRetry={() => eps.refetch()} testId="oa-endpoints-error" /> : (
          <table className="w-full text-[13px]">
            <thead className="text-left text-[12px] text-slate-500"><tr><th className={th}>环境</th><th className={th}>地址</th><th className={th}>订阅事件</th><th className={th}>签名密钥</th><th className={th}>状态</th><th></th></tr></thead>
            <tbody>
              {(["test", "live"] as Env[]).map((env) => {
                const ep = eps.data?.items.find((x) => x.env === env);
                return (
                  <tr key={env} className="border-t border-slate-100 align-top" data-testid={`oa-ep-${env}`}>
                    <td className="py-2 pr-3">{ENV_LABEL[env]}</td>
                    <td className="py-2 pr-3 break-all font-mono text-[12px]">{ep?.url || "—"}{ep?.disabled && ep.disabled_reason && <div className="font-sans text-[12px] text-rose-600">停用原因：{ep.disabled_reason}（{fmt(ep.disabled_at)}）</div>}</td>
                    <td className="py-2 pr-3">{ep ? `${ep.events.length} 个` : "—"}</td>
                    <td className="py-2 pr-3 font-mono text-[12px]">{ep?.secret_preview || "—"}</td>
                    <td className="py-2 pr-3">{ep ? status(ep) : <Pill tone="muted">未配置</Pill>}{!!ep?.consecutive_failures && <div className="text-[12px] text-slate-400">连续失败 {ep.consecutive_failures} 次</div>}</td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <Can perm="channel_partners.partners.status_manage">
                        {ep?.url && !ep.disabled && <button type="button" onClick={() => disable(ep)} data-testid={`oa-disable-${env}`} className="rounded-lg border border-rose-300 px-2.5 py-1 text-[12px] font-medium text-rose-600 hover:bg-rose-50">停用推送</button>}
                        {ep?.disabled && <button type="button" disabled={enable.isPending} onClick={() => enable.mutate(env)} data-testid={`oa-enable-${env}`} className="rounded-lg border border-emerald-300 px-2.5 py-1 text-[12px] font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-40">恢复</button>}
                      </Can>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {err && <div role="alert" className="mt-2 rounded-lg bg-rose-50 px-3 py-2 text-[12px] text-rose-700" data-testid="oa-ops-error">{err}</div>}

        {showDeliveries && (
          <div className="mt-4 border-t border-slate-100 pt-3" data-testid="oa-deliveries">
            <div className="mb-2 flex flex-wrap items-center gap-2 text-[12px]">
              <b className="text-[13px] text-slate-800">推送记录</b>
              <select className="h-8 rounded-lg border border-slate-300 px-2" value={dEnv} onChange={(e) => { setDEnv(e.target.value as Env); setDPage(1); }} aria-label="环境" data-testid="oa-d-env"><option value="live">正式</option><option value="test">测试</option></select>
              <select className="h-8 rounded-lg border border-slate-300 px-2" value={dResult} onChange={(e) => { setDResult(e.target.value); setDPage(1); }} aria-label="结果" data-testid="oa-d-result"><option value="">全部结果</option><option value="ok">成功</option><option value="failed">失败</option></select>
            </div>
            {deliveries.isLoading ? <LoadingState /> : deliveries.isError ? <Err what="推送记录" onRetry={() => deliveries.refetch()} testId="oa-deliveries-error" /> : (
              <>
                <table className="w-full text-[13px]">
                  <thead className="text-left text-[12px] text-slate-500"><tr><th className={th}>时间</th><th className={th}>事件</th><th className={th}>订单 / 产品</th><th className={th}>对方返回</th><th className={th}>重试</th><th></th></tr></thead>
                  <tbody>
                    {(deliveries.data?.items ?? []).map((d) => {
                      const ok = d.http_status != null && d.http_status >= 200 && d.http_status < 300;
                      return (
                        <tr key={d.id} className="border-t border-slate-100" data-testid={`oa-delivery-${d.id}`}>
                          <td className="py-2 pr-3 tabular-nums">{fmt(d.created_at)}</td><td className="pr-3"><code>{d.event}</code></td><td className="pr-3">{d.ref ?? "—"}</td>
                          <td className="pr-3"><Pill tone={ok ? "success" : d.http_status == null && d.attempts === 0 ? "muted" : "danger"}>{d.http_status ?? (d.attempts === 0 ? "待发送" : "无响应")}</Pill></td>
                          <td className="pr-3">{d.attempts} / {d.max_attempts}</td>
                          <td className="text-right"><button type="button" onClick={() => setViewDelivery(d)} data-testid={`oa-view-${d.id}`} className="rounded-lg border border-slate-300 px-2.5 py-1 text-[12px] text-slate-600 hover:bg-slate-50">看内容</button></td>
                        </tr>
                      );
                    })}
                    {(deliveries.data?.items.length ?? 0) === 0 && <tr><td colSpan={6} className="py-3 text-center text-slate-400">没有推送记录</td></tr>}
                  </tbody>
                </table>
                {deliveries.data && deliveries.data.total > deliveries.data.per_page && (
                  <div className="mt-2 flex items-center gap-2 text-[12px] text-slate-500">
                    <button type="button" disabled={dPage <= 1} onClick={() => setDPage(dPage - 1)} className="rounded border border-slate-300 px-2 py-0.5 disabled:opacity-40">上一页</button>
                    <span>第 {dPage} 页 · 共 {deliveries.data.total} 条</span>
                    <button type="button" disabled={dPage * deliveries.data.per_page >= deliveries.data.total} onClick={() => setDPage(dPage + 1)} className="rounded border border-slate-300 px-2 py-0.5 disabled:opacity-40">下一页</button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <div className={card} data-testid="oa-ops-logs">
        <div className="mb-2 text-[14px] font-semibold text-slate-900">调用日志（按请求编号查）</div>
        <form className="flex flex-wrap items-center gap-2" onSubmit={(e) => { e.preventDefault(); setSearched(rid.trim()); }}>
          <input className="h-9 w-72 rounded-lg border border-slate-300 px-2 font-mono text-[12px]" placeholder="对方给的请求编号（X-Request-Id）" aria-label="请求编号" value={rid} onChange={(e) => setRid(e.target.value)} data-testid="oa-rid" />
          <button type="submit" className="rounded-lg bg-blue-600 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-blue-700" data-testid="oa-rid-search">查询</button>
          <span className="text-[12px] text-slate-400">只保留 30 天；留空直接查询 = 看最近的调用</span>
        </form>
        {searched !== null && (logs.isLoading ? <LoadingState /> : logs.isError ? <div className="mt-2"><Err what="调用日志" onRetry={() => logs.refetch()} testId="oa-logs-error" /></div> : (
          <table className="mt-3 w-full text-[13px]" data-testid="oa-log-table">
            <thead className="text-left text-[12px] text-slate-500"><tr><th className={th}>时间</th><th className={th}>环境</th><th className={th}>接口</th><th className={th}>返回</th><th className={th}>耗时</th><th className={th}>来源 IP</th><th></th></tr></thead>
            <tbody>
              {(logs.data?.items ?? []).map((l: AdminCallLog) => (
                <tr key={l.request_id} className="border-t border-slate-100" data-testid={`oa-log-${l.request_id}`}>
                  <td className="py-2 pr-3 tabular-nums">{fmt(l.at)}</td><td className="pr-3">{ENV_LABEL[l.env]}</td><td className="pr-3"><b className="text-[11px]">{l.method}</b> <code>{l.path}</code></td>
                  <td className="pr-3"><Pill tone={l.status < 400 ? "success" : "danger"}>{l.status}{l.error_code ? ` ${l.error_code}` : ""}</Pill></td><td className="pr-3">{l.duration_ms} ms</td><td className="pr-3 font-mono text-[12px]">{l.client_ip ?? "—"}</td>
                  <td className="text-right"><button type="button" onClick={() => setLogDetail({ id: l.request_id })} data-testid={`oa-log-view-${l.request_id}`} className="rounded-lg border border-slate-300 px-2.5 py-1 text-[12px] text-slate-600 hover:bg-slate-50">详情</button></td>
                </tr>
              ))}
              {(logs.data?.items.length ?? 0) === 0 && <tr><td colSpan={7} className="py-3 text-center text-slate-400" data-testid="oa-log-empty">没有找到这个请求编号的记录（日志只保留 30 天）</td></tr>}
            </tbody>
          </table>
        ))}
      </div>

      <div className={card} data-testid="oa-ops-ip">
        <div className="mb-2 text-[14px] font-semibold text-slate-900">IP 白名单（对方自己在门户设置，这里只读）</div>
        {ip.isLoading ? <LoadingState /> : ip.isError ? <Err what="IP 白名单" onRetry={() => ip.refetch()} testId="oa-ip-error" /> : (ip.data?.ip_allowlist.length ?? 0) === 0
          ? <div className="text-[13px] text-slate-500" data-testid="oa-ip-none">未设置（不限来源 IP）</div>
          : <div className="flex flex-wrap gap-1.5" data-testid="oa-ip-list">{ip.data!.ip_allowlist.map((x) => <code key={x} className="rounded bg-slate-100 px-2 py-0.5 text-[12px]">{x}</code>)}</div>}
        <div className="mt-2 text-[12px] text-slate-400">对方说「调用被拒 IP_NOT_ALLOWED」时用来核对；要改让对方自己在开放平台「概览 · 密钥」里改。</div>
      </div>

      {viewDelivery && (
        <Drawer title={`推送内容 · ${viewDelivery.event}`} onClose={() => setViewDelivery(null)} testId="oa-delivery-drawer">
          <div className="mb-2 grid grid-cols-2 gap-1 text-[12px] text-slate-600"><span>状态：{viewDelivery.status}</span><span>对方返回：{viewDelivery.http_status ?? "—"}</span><span>重试：{viewDelivery.attempts} / {viewDelivery.max_attempts}</span><span>下次重试：{fmt(viewDelivery.next_retry_at)}</span></div>
          {viewDelivery.last_error && <div className="mb-2 rounded-lg bg-rose-50 px-3 py-2 text-[12px] text-rose-700" data-testid="oa-delivery-error">最后一次错误：{viewDelivery.last_error}</div>}
          <Json v={viewDelivery.payload} />
        </Drawer>
      )}
      {logDetail && (
        <Drawer title={`调用详情 · ${logDetail.id}`} onClose={() => setLogDetail(null)} testId="oa-log-drawer">
          {detail.isLoading ? <LoadingState /> : detail.isError ? <Err what="调用详情" onRetry={() => detail.refetch()} testId="oa-log-detail-error" /> : detail.data && (
            <div className="flex flex-col gap-2">
              <div className="text-[12px] text-slate-600">{detail.data.method} <code>{detail.data.path}</code> · {detail.data.status}{detail.data.query ? <> · 查询参数 <code data-testid="oa-log-query">{detail.data.query}</code></> : null}</div>
              <div className="text-[12px] font-semibold text-slate-700">请求（游客姓名 / 电话 / 证件号已打码）</div><Json v={detail.data.request} />
              <div className="text-[12px] font-semibold text-slate-700">响应</div><Json v={detail.data.response} />
            </div>
          )}
        </Drawer>
      )}
      {reasonDialog}
    </>
  );
}
